import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChatSection } from '../chat/ChatSection';
import { PopularSearches } from '../search/PopularSearches';
import { useMarkets } from '../../hooks/useMarkets';
import { getVisitorId } from '../../services/fingerprint';
import { 
  Award,
  Goal, 
  Timer, 
  Users, 
  Crosshair,
  ArrowRight,
  TrendingUp,
  Target,
  Zap,
  Activity
} from 'lucide-react';

// Icon mapping based on market ID instead of name
const getMarketIcon = (index: number) => {
  const icons = [
    { icon: Award, color: 'text-yellow-500' },
    { icon: Goal, color: 'text-green-500' },
    { icon: Crosshair, color: 'text-blue-500' },
    { icon: Users, color: 'text-purple-500' },
    { icon: Timer, color: 'text-red-500' },
    { icon: TrendingUp, color: 'text-indigo-500' },
    { icon: Target, color: 'text-pink-500' },
    { icon: Zap, color: 'text-orange-500' },
    { icon: Activity, color: 'text-cyan-500' }
  ];
  return icons[index % icons.length];
};

export const MainLayout: React.FC = () => {
  const { markets, marketSearches, isLoading } = useMarkets();
  const location = useLocation();
  const { query, searchMetadata } = location.state || {};

  // Initialize visitor ID silently
  useEffect(() => {
    getVisitorId().catch(error => {
      console.error('Error initializing visitor ID:', error);
    });
  }, []);

  const topMarkets = markets
    .map(market => ({
      ...market,
      searchCount: marketSearches.filter(s => s.marketId === market.id).length
    }))
    .sort((a, b) => b.searchCount - a.searchCount)
    .slice(0, 3);

  return (
    <div className="flex min-h-[calc(100vh-theme(spacing.32))] items-center justify-center px-0 sm:px-4">
      <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ChatSection 
            initialQuery={query} 
            autoSearch={!!query}
            searchMetadata={searchMetadata}
          />
          
          {/* On mobile: Popular Searches comes before Explore Markets */}
          <div className="block lg:hidden mt-6">
            <PopularSearches />
          </div>

          <div className="mt-6 lg:mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-colors">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Explore Markets
              </h2>
              <Link 
                to="/markets"
                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-2 text-sm font-medium"
              >
                View all markets
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-32 bg-gray-100 dark:bg-gray-700 rounded-lg"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {topMarkets.map((market, index) => {
                  const { icon: IconComponent, color } = getMarketIcon(index);
                  return (
                    <Link
                      key={market.id}
                      to={`/markets/${market.id}`}
                      className="group block p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-white dark:bg-gray-700 rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                          <IconComponent className={`w-5 h-5 ${color}`} />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-white mb-1">
                            {market.name}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {market.searchCount} searches
                          </p>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        
        {/* On desktop: Popular Searches appears in the right column */}
        <div className="lg:col-span-1 hidden lg:block">
          <PopularSearches />
        </div>
      </div>
    </div>
  );
};